import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import EmailIcon from '@mui/icons-material/Email';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { MessageComposerContentItem } from '../../shared/interfaces';
import { copyToClipboard } from '../../utils/clipboard';

interface Props {
  data: MessageComposerContentItem;
}

const channelConfig: Record<string, { icon: React.ReactNode; label: string }> = {
  email: { icon: <EmailIcon fontSize="small" />, label: 'Email' },
  message: { icon: <ChatBubbleOutlineIcon fontSize="small" />, label: 'Mensagem' },
  other: { icon: <EditNoteIcon fontSize="small" />, label: 'Texto' },
};

const MessageComposerPreview: React.FC<Props> = ({ data }) => {
  const { channel, versions } = data;
  const [activeTab, setActiveTab] = useState(0);
  const [isCopied, setIsCopied] = useState(false);
  const [sendMenuOpen, setSendMenuOpen] = useState(false);
  const [sendMode, setSendMode] = useState<'gmail' | 'mail'>('gmail');
  const bodyRef = useRef<HTMLDivElement>(null);
  const sendMenuRef = useRef<HTMLDivElement>(null);

  const config = channelConfig[channel] || channelConfig.other;
  const activeVersion = versions[activeTab];
  const showTabs = versions.length > 1;
  const isEmail = channel === 'email';

  // Close send menu on outside click
  useEffect(() => {
    if (!sendMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (sendMenuRef.current && !sendMenuRef.current.contains(e.target as Node)) {
        setSendMenuOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [sendMenuOpen]);

  // versions can be empty (or activeTab can point past the end) while a
  // message_composer block is still streaming/partial — render nothing rather
  // than dereferencing an undefined version. See CHAT-MARITACA-2A.
  if (!activeVersion) return null;

  const getPlainBody = () => bodyRef.current?.textContent || activeVersion.body;

  const handleCopy = () => {
    let textToCopy = '';
    if (isEmail && activeVersion.subject) {
      textToCopy += `Assunto: ${activeVersion.subject}\n\n`;
    }
    textToCopy += getPlainBody();

    copyToClipboard(textToCopy).then((ok) => {
      if (!ok) return;
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    });
  };

  const openEmail = (mode: 'gmail' | 'mail') => {
    const subject = encodeURIComponent(activeVersion.subject || '');
    const body = encodeURIComponent(getPlainBody());
    if (mode === 'gmail') {
      window.open(`https://mail.google.com/mail/?view=cm&su=${subject}&body=${body}`, '_blank');
    } else {
      window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    }
  };

  const handleSend = () => {
    openEmail(sendMode);
    setSendMenuOpen(false);
  };

  const handleSwitchMode = () => {
    const newMode = sendMode === 'gmail' ? 'mail' : 'gmail';
    setSendMode(newMode);
    setSendMenuOpen(false);
  };

  const sendLabels = { gmail: 'Enviar via Gmail', mail: 'Abrir com Mail' };

  return (
    <Container>
      <Header>
        <ChannelBadge>
          {config.icon}
          <span>{config.label}</span>
        </ChannelBadge>
      </Header>

      {showTabs && (
        <TabBar>
          {versions.map((version, index) => (
            <Tab
              key={index}
              $active={index === activeTab}
              onClick={() => setActiveTab(index)}
            >
              {version.label}
            </Tab>
          ))}
        </TabBar>
      )}

      <ContentArea>
        {activeVersion.description && (
          <Description>{activeVersion.description}</Description>
        )}

        {isEmail && activeVersion.subject && (
          <SubjectLine>
            <strong>Assunto:</strong> {activeVersion.subject}
          </SubjectLine>
        )}

        <Body ref={bodyRef}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {activeVersion.body}
          </ReactMarkdown>
        </Body>

        <ActionBar>
          {isEmail && (
            <SendButtonGroup ref={sendMenuRef}>
              <SendButton onClick={handleSend}>
                {sendMode === 'gmail' ? <GmailIcon /> : <EmailIcon style={{ fontSize: 16 }} />}
                {sendLabels[sendMode]}
              </SendButton>
              <SendDropdown onClick={() => setSendMenuOpen(!sendMenuOpen)}>
                <ArrowDropDownIcon fontSize="small" />
              </SendDropdown>
              {sendMenuOpen && (
                <SendMenu>
                  <SendMenuItem onClick={handleSwitchMode}>
                    {sendMode === 'gmail' ? <EmailIcon style={{ fontSize: 16 }} /> : <GmailIcon />}
                    {sendLabels[sendMode === 'gmail' ? 'mail' : 'gmail']}
                  </SendMenuItem>
                </SendMenu>
              )}
            </SendButtonGroup>
          )}
          <CopyButton onClick={handleCopy} $copied={isCopied}>
            {isCopied ? (
              <>
                <CheckIcon fontSize="small" />
                Copiado!
              </>
            ) : (
              <>
                <ContentCopyIcon fontSize="small" />
                Copiar
              </>
            )}
          </CopyButton>
        </ActionBar>
      </ContentArea>
    </Container>
  );
};

// Gmail logo icon
const GmailIcon = () => (
  <svg width="16" height="13" viewBox="0 0 256 193" style={{ flexShrink: 0 }}>
    <path d="M58.182 192.05V93.14L27.507 65.077 0 49.504v125.091c0 9.658 7.825 17.455 17.455 17.455h40.727z" fill="#4285F4"/>
    <path d="M197.818 192.05h40.727c9.659 0 17.455-7.826 17.455-17.455V49.505l-31.156 17.837-27.026 25.798v98.91z" fill="#34A853"/>
    <path d="M58.182 93.14l-4.174-38.647 4.174-36.989L128 69.868l69.818-52.364 4.669 34.992-4.669 40.644L128 145.504z" fill="#EA4335"/>
    <path d="M197.818 17.504V93.14L256 49.504V26.231c0-21.585-24.64-33.89-41.89-20.945l-16.292 12.218z" fill="#FBBC04"/>
    <path d="M0 49.504l26.759 20.07L58.182 93.14V17.504L41.89 5.286C24.61-7.66 0 4.646 0 26.23v23.273z" fill="#C5221F"/>
  </svg>
);

export default MessageComposerPreview;

// Styled Components

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.borderGrey};
  border-radius: 12px;
  overflow: hidden;
  margin: 12px 0;
  background: ${({ theme }) => theme.input_surface_bg};
  width: 100%;
`;

const Header = styled.div`
  padding: 12px 16px 0;
`;

const ChannelBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.secondary_text_color};
  font-size: 13px;
  font-weight: 500;

  svg {
    font-size: 16px;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  padding: 12px 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.borderGrey};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ theme, $active }) => $active ? theme.blue_color : theme.secondary_text_color};
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.blue_color : 'transparent'};
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.blue_color};
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const ContentArea = styled.div`
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
`;

const Description = styled.div`
  color: ${({ theme }) => theme.secondary_text_color};
  font-size: 13px;
  font-style: italic;
  margin-bottom: 12px;
`;

const SubjectLine = styled.div`
  color: ${({ theme }) => theme.font_color};
  font-size: 14px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.borderGrey};
`;

const Body = styled.div`
  color: ${({ theme }) => theme.font_color};
  font-size: 14px;
  line-height: 1.6;

  p {
    margin: 0 0 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul, ol {
    margin: 4px 0;
    padding-left: 20px;
  }

  strong {
    font-weight: 600;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.borderGrey};
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid ${({ theme }) => theme.borderGrey};
  border-radius: 8px;
  background: ${({ theme, $copied }) => $copied ? 'rgba(34, 197, 94, 0.1)' : 'transparent'};
  color: ${({ theme, $copied }) => $copied ? '#22c55e' : theme.secondary_text_color};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }

  svg {
    font-size: 16px;
  }
`;

const SendButtonGroup = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.borderGrey};
  border-radius: 8px;
  overflow: visible;
`;

const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.secondary_text_color};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const SendDropdown = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 6px 4px;
  border: none;
  border-left: 1px solid ${({ theme }) => theme.borderGrey};
  background: transparent;
  color: ${({ theme }) => theme.secondary_text_color};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const SendMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  background: ${({ theme }) => theme.input_surface_bg};
  border: 1px solid ${({ theme }) => theme.borderGrey};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const SendMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;
